import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store';
import { loadEmptyFolders, normalizeFolder } from '../../utils/workflowFolders';
import { useTranslation } from 'react-i18next';
import { be1Fetch } from '../../config/api';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { errorFromResponse } from '../../services/errorText';
import { downloadJson, fileNameOf, ProjectFile } from '../../services/projectTransfer';
import { formatNumber } from '../../utils/formatters';
import { useCapabilities } from '../../services/capabilities';
import ConnectionsDialog from '../connections/ConnectionsDialog';

/**
 * 테라스트림 → 오픈플로 이관 (서버 services/migrate_terastream, docs/MIGRATION_TERASTREAM.md).
 *
 * 1) XML(또는 zip)을 고르고 경로 바꾸기 규칙을 적어 「변환」 — 서버는 아무것도 저장하지 않고 프로젝트 파일과 보고서만 돌려준다.
 * 2) 보고서(그대로 / 참고 / 확인 / 수동 / 오류)와 만들어야 할 연결을 본다.
 * 3) 「가져오기」 — 프로젝트 불러오기와 같은 길로 새 프로젝트를 만든다(정의·SAM유형 포함).
 */

interface ReportItem { level: 'ok' | 'info' | 'warn' | 'manual' | 'error'; code: string; block: string; params: Record<string, string> }
/** status: 이 서버와 대조 — exists · missing · typeDiffers · unknown(목록을 못 읽음). serverType: 이 서버의 종류 */
interface Conn { id: string; conn_type: string; host: string; port: number | null; login: string; schema: string; extra: Record<string, any>; status?: string; serverType?: string }
/** 분석기가 블록을 어떻게 읽었는가 — 종류·점수·값마다 붙인 역할 (서버 migrate_analyze) */
interface InterpBlock { id: string; name: string; tag: string; type: string; kind: string; scores: Record<string, number>;
  roles: Array<{ name: string; role: string; dir: string; value: string }>; unknown: Array<{ name: string; value: string }> }
interface Interpretation { connections: any[]; layouts: Array<{ id: string; format: string; columns: number; element: string }>; params: number; settings: number; blocks: InterpBlock[] }
interface Result { projectFile: ProjectFile; report: ReportItem[]; connections: Conn[]; stats: Record<string, number>; interpretation?: Interpretation }

/** 가져올 곳 — 새 프로젝트(테라스트림 프로젝트마다 하나) 또는 지금 프로젝트, 그리고 워크플로를 둘 폴더 */
export interface MigrateTarget {
  /** 언제나 지금 프로젝트 — 새 프로젝트를 만들지 않는다 */
  mode: 'current';
  folder: string;
  /** 여러 개를 가져올 때 테라스트림 프로젝트마다 하위 폴더(폴더/프로젝트 이름) */
  perProject: boolean;
}

interface Props {
  onClose: () => void;
  /** 고른 결과들을 가져온다 — useProjectTransfer 의 불러오기 */
  onImport: (files: ProjectFile[], target: MigrateTarget) => void;
}

const LEVELS: ReportItem['level'][] = ['error', 'manual', 'warn', 'info', 'ok'];
const LEVEL_STYLE: Record<ReportItem['level'], string> = {
  error: 'bg-red-100 text-red-700', manual: 'bg-purple-100 text-purple-700', warn: 'bg-amber-100 text-amber-800',
  info: 'bg-gray-100 text-gray-600', ok: 'bg-green-100 text-green-700',
};

async function toBase64(f: File): Promise<string> {
  const bytes = new Uint8Array(await f.arrayBuffer());
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + 0x8000)));
  return btoa(bin);
}

/** "원래 경로 => 바꿀 경로" 한 줄에 하나 */
export function parsePathRules(text: string): Array<{ from: string; to: string }> {
  return text.split('\n').map((l) => l.split('=>')).filter((p) => p.length === 2 && p[0].trim())
    .map(([from, to]) => ({ from: from.trim(), to: to.trim() }));
}

const TeraStreamImportDialog: React.FC<Props> = ({ onClose, onImport }) => {
  const { t, i18n } = useTranslation();
  const [creating, setCreating] = useState<Conn | null>(null);
  useEscapeKey(onClose, !creating);   // 연결 만들기 창이 떠 있으면 Esc 는 그 창이 받는다
  const [files, setFiles] = useState<File[]>([]);
  const [rules, setRules] = useState('');
  /** SAM 파일 인코딩 — 자동(서버에서 파일이 보이면 판별, 안 보이면 UTF-8) 또는 직접 고름. export 에는 인코딩이 없다 */
  const [encoding, setEncoding] = useState('auto');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [out, setOut] = useState<{ results: Result[]; report: ReportItem[] } | null>(null);
  const [pick, setPick] = useState(0);
  /** 가져올 결과 — 처음에는 오류 없는 것 전부 */
  const [chosen, setChosen] = useState<Set<number>>(new Set());
  const project = useSelector((s: RootState) => s.project.projects.find((p) => p.id === s.project.selectedProjectId));
  const workflows = useSelector((s: RootState) => s.project.workflows);
  /** 가져올 곳은 언제나 지금 프로젝트 — 테라스트림 프로젝트는 오픈플로의 **워크플로**가 된다. 프로젝트를 새로 만들지 않는다(2026-10-12) */
  const mode = 'current' as const;
  const [folder, setFolder] = useState(t('layout:migrate.defaultFolder'));
  const [perProject, setPerProject] = useState(true);
  /** 지금 프로젝트의 폴더 — 고를 수 있게(상위 경로까지). 없는 이름을 적으면 새 폴더가 된다 */
  const folders = useMemo(() => {
    if (!project) return [] as string[];
    const all = new Set<string>();
    const add = (f: string) => { const parts = normalizeFolder(f).split('/').filter(Boolean); parts.forEach((_, i) => all.add(parts.slice(0, i + 1).join('/'))); };
    workflows.filter((w) => w.projectId === project.id).forEach((w) => add(w.folder || ''));
    loadEmptyFolders(project.id).forEach(add);
    return Array.from(all).sort();
  }, [project, workflows]);
  useEffect(() => {
    if (out) setChosen(new Set(out.results.map((r, i) => (r.stats.error ? -1 : i)).filter((i) => i >= 0)));
  }, [out]);
  const picked = (out?.results || []).filter((_, i) => chosen.has(i));
  const toggle = (i: number) => setChosen((c) => { const n = new Set(c); if (n.has(i)) n.delete(i); else n.add(i); return n; });
  const [hide, setHide] = useState<Record<string, boolean>>({ ok: true, info: true });
  const [interpOpen, setInterpOpen] = useState(false);
  const caps = useCapabilities();
  /** 없는 연결을 여기서 바로 만든다 — 무설치판 관리자만(웹 버전은 Airflow 에서 만든다) */
  const canCreate = !!caps?.admin && !!caps?.localConnections;

  const convert = async () => {
    setBusy(true); setError(''); setOut(null);
    try {
      const payload = { files: await Promise.all(files.map(async (f) => ({ name: f.name, content: await toBase64(f) }))),
        options: { pathRules: parsePathRules(rules), lang: i18n.language, encoding } };
      const r = await be1Fetch('/api/migrate/terastream', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }, { retries: 0 });
      if (!r.ok) throw await errorFromResponse(r, `HTTP ${r.status}`);
      setOut(await r.json());
      setPick(0);
    } catch (e: any) {
      setError(t('layout:migrate.failed', { message: e?.message || String(e) }));
    } finally {
      setBusy(false);
    }
  };

  const msg = (it: ReportItem) => t(`layout:migrate.report.${it.code}`, { block: it.block, ...it.params, defaultValue: `${it.code} ${JSON.stringify(it.params)}` });
  const cur = out?.results[pick];
  const items = useMemo(() => [...(out?.report || []), ...(cur?.report || [])]
    .filter((x) => !hide[x.level])
    .sort((a, b) => LEVELS.indexOf(a.level) - LEVELS.indexOf(b.level)), [out, cur, hide]);

  return (
    <>
    {creating && <ConnectionsDialog create={{ id: creating.id, conn_type: creating.conn_type }} onClose={() => { setCreating(null); convert(); }} />}
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-30" onMouseDown={onClose}>
      <div className="bg-white rounded shadow-lg w-[920px] max-w-[96vw] max-h-[90vh] flex flex-col" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div>
            <div className="text-sm font-semibold text-gray-800">{t('layout:migrate.title')}</div>
            <div className="text-xs text-gray-500">{t('layout:migrate.subtitle')}</div>
          </div>
          <button className="px-2 py-1 text-xs rounded hover:bg-gray-100 text-gray-600" onClick={onClose} title={t('common:ui.close')}>✕</button>
        </div>

        <div className="p-4 overflow-auto flex-1 text-sm">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">{t('layout:migrate.files')}</label>
              <input type="file" multiple accept=".xml,.zip" className="text-xs"
                onChange={(e) => { setFiles(Array.from(e.target.files || [])); setOut(null); }} />
              <ul className="mt-1 text-xs text-gray-600 max-h-24 overflow-auto">
                {files.map((f) => <li key={f.name} className="truncate">· {f.name} <span className="text-gray-400">({formatNumber(f.size)} B)</span></li>)}
              </ul>
              <div className="text-[0.6875rem] text-gray-500 mt-1">{t('layout:migrate.filesHint')}</div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">{t('layout:migrate.pathRules')}</label>
              <textarea className="w-full border border-gray-300 rounded px-2 py-1 text-xs font-mono bg-white" rows={3} value={rules}
                placeholder={t('layout:migrate.pathRulesPlaceholder')} onChange={(e) => setRules(e.target.value)} />
              <div className="text-[0.6875rem] text-gray-500">{t('layout:migrate.pathRulesHint')}</div>
              <label className="flex items-center gap-2 mt-2 text-xs text-gray-700">
                <span className="font-semibold text-gray-600">{t('layout:migrate.encoding')}</span>
                <select className="border border-gray-300 rounded px-1.5 py-0.5 bg-white" value={encoding} onChange={(e) => { setEncoding(e.target.value); setOut(null); }}>
                  <option value="auto">{t('layout:migrate.encodingAuto')}</option>
                  <option value="UTF-8">UTF-8</option>
                  <option value="CP949">MS949 (CP949)</option>
                  <option value="EUC-KR">EUC-KR</option>
                </select>
              </label>
              <div className="text-[0.6875rem] text-gray-500">{t('layout:migrate.encodingHint')}</div>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <button className="px-3 py-1 text-xs rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50" disabled={!files.length || busy} onClick={convert}>
              {busy ? t('layout:migrate.converting') : t('layout:migrate.convert')}
            </button>
            {error && <span className="text-xs text-red-600">{error}</span>}
          </div>

          {out && (
            <div className="mt-4 border-t border-gray-200 pt-3">
              {out.results.length === 0 && <div className="text-xs text-red-600 mb-2">{t('layout:migrate.noResult')}</div>}
              {/* 변환된 프로젝트 — 체크한 것을 가져온다. 이름을 누르면 그 보고서를 본다 */}
              {out.results.length > 0 && (
                <div className="mb-3">
                  <div className="text-xs font-semibold text-gray-600 mb-1">{t('layout:migrate.results', { n: formatNumber(out.results.length) })}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {out.results.map((r, i) => (
                      <div key={i} className={`flex items-center gap-1 px-2 py-0.5 text-xs rounded border ${i === pick ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}>
                        <input type="checkbox" checked={chosen.has(i)} onChange={() => toggle(i)} title={t('layout:migrate.choose')} />
                        <button type="button" onClick={() => setPick(i)} className="text-gray-800">{r.projectFile.project.name}</button>
                        <span className="text-gray-400">· {formatNumber(r.projectFile.workflows.length)}</span>
                        {r.stats.error > 0 && <span className="text-red-600">⚠ {formatNumber(r.stats.error)}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {cur && (
                <>
                  <div className="flex items-center gap-3 flex-wrap mb-2">
                    <span className="font-semibold text-gray-800">{cur.projectFile.project.name}</span>
                    <span className="text-xs text-gray-500">{t('layout:migrate.summary', {
                      workflows: formatNumber(cur.projectFile.workflows.length),
                      nodes: formatNumber(cur.projectFile.workflows.reduce((n, w) => n + (w.canvas?.nodes.length || 0), 0)),
                      types: formatNumber(cur.projectFile.samTypes?.length || 0), defs: formatNumber(cur.projectFile.dataDefinitions?.length || 0),
                    })}</span>
                  </div>
                  <div className="flex items-center gap-1.5 mb-2">
                    {LEVELS.map((lv) => (
                      <button key={lv} className={`px-2 py-0.5 rounded text-xs ${LEVEL_STYLE[lv]} ${hide[lv] ? 'opacity-40 line-through' : ''}`}
                        title={t('layout:migrate.toggleLevel')} onClick={() => setHide((h) => ({ ...h, [lv]: !h[lv] }))}>
                        {t(`layout:migrate.level.${lv}`)} {formatNumber(cur.stats[lv] || 0)}
                      </button>
                    ))}
                  </div>
                  <table className="w-full text-xs mb-3">
                    <tbody>
                      {items.map((it, i) => (
                        <tr key={i} className="border-b border-gray-100 align-top">
                          <td className="py-0.5 pr-2 w-14"><span className={`px-1.5 rounded ${LEVEL_STYLE[it.level]}`}>{t(`layout:migrate.level.${it.level}`)}</span></td>
                          <td className="py-0.5 pr-2 w-40 font-mono text-gray-500 truncate" title={it.block}>{it.block}</td>
                          <td className="py-0.5 text-gray-700 whitespace-pre-wrap">{msg(it)}</td>
                        </tr>
                      ))}
                      {!items.length && <tr><td className="py-2 text-gray-400">{t('layout:migrate.noItems')}</td></tr>}
                    </tbody>
                  </table>
                  {/* 이렇게 읽었습니다 — 요소 이름이 아니라 구조·값 모양으로 읽으므로, 무엇을 어떤 역할로 봤는지 보여 준다.
                      틀렸으면 규칙 파일(데이터 폴더 migrate/terastream_rules.json)에 낱말을 더해 가르친다 */}
                  {cur.interpretation && (
                    <div className="mb-3">
                      <button type="button" className="text-xs text-blue-700 hover:underline" onClick={() => setInterpOpen((v) => !v)}>
                        {interpOpen ? '▾' : '▸'} {t('layout:migrate.interp.title', { n: formatNumber(cur.interpretation.blocks.length) })}
                      </button>
                      {interpOpen && (
                        <div className="mt-1 border border-gray-200 rounded p-2 text-xs">
                          <div className="text-[0.6875rem] text-gray-500 mb-1">{t('layout:migrate.interp.hint')}</div>
                          {cur.interpretation.blocks.map((b, i) => (
                            <div key={i} className="mb-2">
                              <div className="font-semibold text-gray-800">
                                <span className="font-mono text-gray-500">{b.tag}{b.type ? `[${b.type}]` : ''}</span> {b.name || b.id}
                                <span className="ml-2 px-1.5 rounded bg-blue-100 text-blue-800">{t(`layout:migrate.kind.${b.kind}`, { defaultValue: b.kind })}</span>
                                <span className="ml-1 text-gray-400 font-normal">{Object.entries(b.scores).filter(([, v]) => v > 0).map(([k, v]) => `${t(`layout:migrate.kind.${k}`, { defaultValue: k })} ${v}`).join(' · ')}</span>
                              </div>
                              <table className="w-full ml-2">
                                <tbody>
                                  {b.roles.map((r, j) => (
                                    <tr key={j}><td className="w-36 font-mono text-gray-500 align-top">{r.name}</td>
                                      <td className="w-28 align-top">{t(`layout:migrate.role.${r.role}`, { defaultValue: r.role })}{r.dir ? ` (${t(`layout:migrate.dir.${r.dir}`)})` : ''}</td>
                                      <td className="text-gray-700 truncate max-w-[420px]" title={r.value}>{r.value}</td></tr>
                                  ))}
                                  {b.unknown.map((u, j) => (
                                    <tr key={`u${j}`} className="text-amber-700"><td className="w-36 font-mono align-top">{u.name}</td><td>{t('layout:migrate.role.unknown')}</td><td className="truncate max-w-[420px]">{u.value}</td></tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {cur.connections.length > 0 && (
                    <>
                      <div className="text-xs font-semibold text-gray-600 mb-1">{t('layout:migrate.connections')}</div>
                      <table className="w-full text-xs mb-1">
                        <thead><tr className="text-left text-gray-500 border-b border-gray-200">
                          <th className="py-0.5">ID</th><th>{t('layout:migrate.connType')}</th><th>{t('layout:migrate.connHost')}</th><th>{t('layout:migrate.connSchema')}</th><th>{t('layout:migrate.connLogin')}</th><th>{t('layout:migrate.connStatus')}</th>
                        </tr></thead>
                        <tbody>
                          {cur.connections.map((c) => (
                            <tr key={c.id} className="border-b border-gray-100 font-mono">
                              <td className="py-0.5">{c.id}</td><td>{c.conn_type}</td><td>{c.host}{c.port ? `:${c.port}` : ''}</td>
                              <td>{c.schema || (c.extra?.service_name ? `service_name=${c.extra.service_name}` : '')}</td><td>{c.login}</td>
                              <td className="font-sans whitespace-nowrap">
                                <span className={c.status === 'exists' ? 'text-green-700' : c.status === 'unknown' || !c.status ? 'text-gray-500' : 'text-amber-700'}>
                                  {t(`layout:migrate.connState.${c.status || 'unknown'}`, { type: c.serverType || '' })}
                                </span>
                                {canCreate && c.status === 'missing' && (
                                  <button type="button" className="ml-2 px-1.5 rounded border border-blue-400 text-blue-700 hover:bg-blue-50" onClick={() => setCreating(c)}>
                                    {t('layout:migrate.connCreate')}
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="text-[0.6875rem] text-amber-700">
                        {t(canCreate ? 'layout:migrate.connectionsHintLocal' : caps?.localConnections ? 'layout:migrate.connectionsHintAdmin' : 'layout:migrate.connectionsHintAirflow')}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* 가져올 곳 — 지금 프로젝트, 워크플로를 둘 폴더(고르거나 새 이름) */}
        {out && out.results.length > 0 && (
          <div className="px-4 py-2 border-t border-gray-200 text-xs text-gray-700 flex flex-wrap items-center gap-x-4 gap-y-1.5">
            <span className="font-semibold text-gray-600">{t('layout:migrate.target')}</span>
            {project
              ? <span>{t('layout:migrate.targetCurrent', { name: project.name })}</span>
              : <span className="text-amber-700">{t('layout:migrate.noCurrent')}</span>}
            <label className="flex items-center gap-1">
              {t('layout:migrate.folder')}
              <input className="w-48 border border-gray-300 rounded px-1.5 py-0.5 bg-white" list="ts-folder-options" value={folder}
                placeholder={t('layout:migrate.folderPlaceholder')} onChange={(e) => setFolder(e.target.value)} />
              <datalist id="ts-folder-options">{folders.map((f) => <option key={f} value={f} />)}</datalist>
            </label>
            {picked.length > 1 && (
              <label className="flex items-center gap-1 cursor-pointer">
                <input type="checkbox" checked={perProject} onChange={(e) => setPerProject(e.target.checked)} />{t('layout:migrate.perProject')}
              </label>
            )}
            <span className="text-[0.6875rem] text-gray-500 w-full">
              {normalizeFolder(folder) && !folders.includes(normalizeFolder(folder))
                ? t('layout:migrate.folderNew', { folder: normalizeFolder(folder) })
                : t('layout:migrate.folderHint')}
            </span>
          </div>
        )}

        <div className="px-4 py-2 border-t border-gray-200 flex items-center justify-between">
          <button className="px-3 py-1 text-xs rounded border border-gray-300 hover:bg-gray-100 text-gray-700 disabled:opacity-50" disabled={!out}
            onClick={() => out && downloadJson(out, `${fileNameOf(cur?.projectFile.project.name || 'terastream')}.migration-report.json`)}>
            {t('layout:migrate.downloadReport')}
          </button>
          <div className="flex gap-2">
            <button className="px-3 py-1 text-xs rounded bg-gray-200 hover:bg-gray-300 text-gray-700" onClick={onClose}>{t('common:ui.cancel')}</button>
            <button className="px-3 py-1 text-xs rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50" disabled={!picked.length || !project}
              onClick={() => onImport(picked.map((r) => r.projectFile), { mode, folder: normalizeFolder(folder), perProject: picked.length > 1 && perProject })}>
              {t('layout:migrate.importN', { n: formatNumber(picked.length) })}
            </button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default TeraStreamImportDialog;
